import { useState } from "react";
import { useGetMyBlockStatusQuery, useSubmitAppealMutation } from "../../store/slices/api";

/**
 * Banner shown when a user is blocked from a specific feature.
 * Props:
 *   featureKey  — one of: "is_bulletin_blocked", "is_marketplace_blocked",
 *                 "is_connect_blocked", "is_tree_blocked", "is_ai_blocked",
 *                 "is_messaging_blocked", "is_footprint_blocked",
 *                 "is_scrapbook_blocked"
 *                 (the backend's models.FEATURE_BLOCKS is the source of truth)
 *   featureName — display name, e.g. "Bulletin Board"
 *   individualId — the current user's individual ID
 *   mode        — "full" (replaces content) or "banner" (shows above content)
 *   children    — optional: the normal content to show when NOT blocked
 */
export default function BlockedFeatureBanner({
  featureKey,
  featureName,
  individualId,
  mode = "full",
  children,
}) {
  const { data: blockStatus } = useGetMyBlockStatusQuery(individualId, {
    skip: !individualId,
  });
  const [submitAppeal, { isLoading: appealLoading }] = useSubmitAppealMutation();
  const [appealText, setAppealText] = useState("");
  const [appealSent, setAppealSent] = useState(false);
  const [appealError, setAppealError] = useState(null);
  const [showAppealForm, setShowAppealForm] = useState(false);

  // Fail open: while the block check is loading, or if it fails to load (e.g. a
  // slow/waking server), show the feature instead of a blank page. If the check
  // later resolves to "blocked", the banner replaces the content then. The block
  // is admin moderation, not a security gate, so showing content briefly is safe.
  if (!blockStatus) {
    return children || null;
  }

  const isBlocked = blockStatus[featureKey];

  if (!isBlocked) {
    return children || null;
  }

  const reason =
    blockStatus.feature_blocked_reason || blockStatus.messaging_blocked_reason;

  const handleAppeal = async () => {
    if (!appealText.trim()) return;
    setAppealError(null);
    try {
      await submitAppeal({
        individual_id: individualId,
        content: appealText.trim(),
      }).unwrap();
      setAppealSent(true);
      setAppealText("");
      setShowAppealForm(false);
    } catch (err) {
      setAppealError(
        err?.data?.error || "Failed to submit appeal. Please try again."
      );
    }
  };

  const banner = (
    <div className="rounded-lg border border-red-300 bg-red-50 dark:bg-red-900/20 dark:border-red-700 p-6 text-center">
      <div className="text-[40px] mb-3">🚫</div>
      <h3 className="font-display text-lg font-bold text-red-800 dark:text-red-300 mb-2">
        {featureName} Access Suspended
      </h3>
      <p className="text-red-700 dark:text-red-400 text-sm mb-4">
        Your access to {featureName} has been suspended by an administrator.
        {reason && (
          <span className="block mt-1 italic">Reason: {reason}</span>
        )}
      </p>

      {appealSent ? (
        <p className="text-green-700 dark:text-green-400 text-sm font-medium">
          Your appeal has been submitted. An administrator will review it.
        </p>
      ) : showAppealForm ? (
        <div className="mt-3 max-w-md mx-auto">
          <textarea
            className="w-full rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-200 p-3 text-sm focus:ring-2 focus:ring-dupontGold focus:border-transparent"
            rows={3}
            maxLength={600}
            placeholder="Explain why you believe your access should be restored..."
            value={appealText}
            onChange={(e) => setAppealText(e.target.value)}
          />
          {appealError && (
            <p className="text-red-600 dark:text-red-400 text-[13px] mt-1">{appealError}</p>
          )}
          <div className="flex gap-2 mt-2 justify-center">
            <button
              onClick={handleAppeal}
              disabled={appealLoading || !appealText.trim()}
              className="px-4 py-2 bg-dupontGold text-white rounded-md text-sm font-medium hover:bg-dupontGold/90 disabled:opacity-50"
            >
              {appealLoading ? "Sending..." : "Submit Appeal"}
            </button>
            <button
              onClick={() => setShowAppealForm(false)}
              className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          onClick={() => setShowAppealForm(true)}
          className="px-4 py-2 border border-dupontGold text-dupontGold rounded-md text-sm font-medium hover:bg-dupontGold/10"
        >
          Appeal This Decision
        </button>
      )}
    </div>
  );

  if (mode === "full") {
    return (
      <div className="flex items-center justify-center min-h-[400px] p-6">
        {banner}
      </div>
    );
  }

  // mode === "banner" — show the banner above the children
  return (
    <>
      <div className="mb-4">{banner}</div>
      {children}
    </>
  );
}
